import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback, useMemo } from 'react'

import { ChecklistItem } from 'src/analytics'
import {
  organizationsAccessListOptions,
  organizationsAccessListQueryKey,
  organizationsPermissionsUserListQueryKey,
  organizationsRolesReplaceCreateMutation,
  organizationsRolesRetrieveOptions,
  organizationsWorkspacePermissionsListOptions,
  organizationsWorkspaceRolesCreateMutation,
  organizationsWorkspaceRolesDestroyMutation,
  organizationsWorkspaceRolesListOptions,
  organizationsWorkspaceRolesListQueryKey,
  organizationsWorkspaceRolesPartialUpdateMutation,
  type WorkspacePermissionCatalog,
  type WorkspaceRole
} from 'src/api/main'
import { useReportJourneyStep } from 'src/features/workspaces/onboarding'
import { slugifyPermissionCode } from 'src/utils/string'

import { MEMBERS_LIST_PAGE_SIZE, ROLES_LIST_PAGE_SIZE } from './constants'
import type { RoleFormValues, RoleMember } from './types'

const rolesListQueryArg = (workspaceId: number) => ({
  query: { organization: workspaceId, per_page: ROLES_LIST_PAGE_SIZE }
})

/**
 * Workspace roles from `/organizations/workspace-roles/` — the endpoint
 * excludes application roles server-side, so no client filtering is needed
 * and every row carries `users_count` plus its `WorkspacePermissionCatalog[]`.
 */
export const useWorkspaceRoles = (workspaceId: number | null) => {
  const reportStep = useReportJourneyStep(workspaceId ?? undefined)
  const queryClient = useQueryClient()
  const enabled = workspaceId != null

  const listQuery = useQuery({
    ...organizationsWorkspaceRolesListOptions(
      enabled ? rolesListQueryArg(workspaceId) : { query: { organization: 0 } }
    ),
    enabled
  })

  const invalidate = useCallback(() => {
    if (workspaceId != null) {
      void queryClient.invalidateQueries({
        queryKey: organizationsWorkspaceRolesListQueryKey(
          rolesListQueryArg(workspaceId)
        )
      })
      // A role edit can change what the signed-in user themselves may do.
      void queryClient.invalidateQueries({
        queryKey: organizationsPermissionsUserListQueryKey({
          query: { organization: workspaceId }
        })
      })
    }
    // Member rows carry their role chips.
    void queryClient.invalidateQueries({
      queryKey: organizationsAccessListQueryKey()
    })
  }, [queryClient, workspaceId])

  const createRoleMutation = useMutation({
    ...organizationsWorkspaceRolesCreateMutation(),
    onSuccess: () => {
      invalidate()
      reportStep(ChecklistItem.ROLES)
    }
  })

  const updateRoleMutation = useMutation({
    ...organizationsWorkspaceRolesPartialUpdateMutation(),
    onSuccess: () => {
      invalidate()
      reportStep(ChecklistItem.ROLES)
    }
  })

  const deleteRoleMutation = useMutation({
    ...organizationsWorkspaceRolesDestroyMutation(),
    onSuccess: invalidate
  })

  // No workspace-scoped replace endpoint exists; workspace roles are the same
  // OrganizationRole rows, so the generic one still reassigns their members.
  const replaceRoleMutation = useMutation({
    ...organizationsRolesReplaceCreateMutation(),
    onSuccess: invalidate
  })

  const roles: WorkspaceRole[] = useMemo(
    () => listQuery.data?.results ?? [],
    [listQuery.data]
  )

  const createRole = useCallback(
    (values: RoleFormValues) => {
      if (workspaceId == null) {
        return Promise.reject(new Error('No workspace'))
      }

      return createRoleMutation.mutateAsync({
        body: {
          organization: workspaceId,
          name: values.name,
          // `codename` is required here (it was optional on the old generic
          // endpoint), so derive one from the name when the form omits it.
          codename: values.codename || slugifyPermissionCode(values.name),
          description: values.description || undefined,
          permissions_ids: values.permissions_ids ?? []
        }
      })
    },
    [createRoleMutation, workspaceId]
  )

  const updateRole = useCallback(
    (id: number, values: RoleFormValues) =>
      updateRoleMutation.mutateAsync({
        path: { id },
        body: {
          name: values.name,
          codename: values.codename || undefined,
          description: values.description || undefined,
          // Left out entirely when the caller has no opinion, so a rename
          // never clears the role's permission set.
          ...(values.permissions_ids
            ? { permissions_ids: values.permissions_ids }
            : {})
        }
      }),
    [updateRoleMutation]
  )

  /** Permission-only patch — what the matrix saves per role column. */
  const updateRolePermissions = useCallback(
    (id: number, permissionIds: number[]) =>
      updateRoleMutation.mutateAsync({
        path: { id },
        body: { permissions_ids: permissionIds }
      }),
    [updateRoleMutation]
  )

  const deleteRole = useCallback(
    (id: number) => deleteRoleMutation.mutateAsync({ path: { id } }),
    [deleteRoleMutation]
  )

  const replaceAndDelete = useCallback(
    async (id: number, replacementRoleId: number) => {
      await replaceRoleMutation.mutateAsync({
        path: { id },
        body: { replacement_role: replacementRoleId }
      })
      await deleteRoleMutation.mutateAsync({ path: { id } })
    },
    [deleteRoleMutation, replaceRoleMutation]
  )

  return {
    roles,
    isLoading: listQuery.isLoading,
    isError: listQuery.isError,
    error: listQuery.error,
    refetch: listQuery.refetch,
    createRole,
    updateRole,
    updateRolePermissions,
    deleteRole,
    replaceAndDelete,
    isMutating:
      createRoleMutation.isPending ||
      updateRoleMutation.isPending ||
      deleteRoleMutation.isPending ||
      replaceRoleMutation.isPending
  }
}

/**
 * Single role detail. Stays on `/organizations/roles/{id}/`: it is the only
 * shape carrying `user_assignments`, which `useRoleMembers` needs to cover
 * roles granted through a group. `workspace-roles` returns no assignments.
 */
export const useWorkspaceRole = (
  roleId: number | null,
  options?: { enabled?: boolean }
) => {
  const enabled = (options?.enabled ?? true) && roleId != null

  return useQuery({
    ...organizationsRolesRetrieveOptions({
      path: { id: roleId ?? 0 }
    }),
    enabled
  })
}

/**
 * The workspace permission catalog — a fixed, read-only set the backend
 * derives from its own enum (`CodeEnum` / `CategoryEnum`). It is the list
 * the role matrix grants from, and the catalog tab renders.
 */
export const useWorkspacePermissions = (workspaceId: number | null) => {
  const enabled = workspaceId != null

  const query = useQuery({
    ...organizationsWorkspacePermissionsListOptions({
      query: { organization: workspaceId ?? 0 }
    }),
    enabled
  })

  const permissions: WorkspacePermissionCatalog[] = useMemo(
    () => query.data ?? [],
    [query.data]
  )

  return {
    permissions,
    isLoading: query.isLoading,
    isError: query.isError
  }
}

/**
 * Members assigned to a specific role, from the role detail's
 * `user_assignments` — the only source that covers roles granted through a
 * group (`source: 'group'`). The workspace access list is still fetched to
 * enrich each row with the member's team and join date; rows whose member
 * falls outside its `MEMBERS_LIST_PAGE_SIZE` cap simply render without those
 * two fields.
 *
 * A member can appear twice when they hold the same role both personally and
 * via a group — Core stores those as separate `UserRole` rows on purpose.
 */
export const useRoleMembers = (
  workspaceId: number | null,
  roleId: number | null
) => {
  const enabled = workspaceId != null && roleId != null

  const query = useQuery({
    ...organizationsAccessListOptions({
      query: enabled
        ? { organization: workspaceId, per_page: MEMBERS_LIST_PAGE_SIZE }
        : { per_page: MEMBERS_LIST_PAGE_SIZE }
    }),
    enabled
  })

  const roleQuery = useWorkspaceRole(roleId, { enabled })

  const members = useMemo(() => {
    if (!enabled) return []
    const assignments = roleQuery.data?.user_assignments ?? []
    const accessById = new Map(
      (query.data?.results ?? []).map((m) => [m.id, m])
    )

    return assignments.map((assignment): RoleMember => {
      const access = accessById.get(assignment.organization_access_id)

      return {
        id: String(assignment.id),
        userId: assignment.user.id,
        name:
          [assignment.user.first_name, assignment.user.last_name]
            .filter(Boolean)
            .join(' ') || assignment.user.email,
        email: assignment.user.email,
        team: access?.teams[0]?.name ?? '—',
        joinedDate: access?.joined_date ?? null,
        source: assignment.source === 'group' ? 'group' : 'personal',
        sourceGroupName: assignment.source_group?.name ?? null
      }
    })
  }, [enabled, query.data, roleQuery.data])

  return {
    members,
    total: roleQuery.data?.users_count ?? members.length,
    isLoading: roleQuery.isLoading,
    isError: roleQuery.isError
  }
}
